import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { FaStore, FaPlay, FaCheckCircle } from 'react-icons/fa';
import { FaSliders } from 'react-icons/fa6';
import styled from 'styled-components';

interface Store {
  id: number;
  uuid: string;
  trading_name: string;
  company_name: string;
}

interface ModalCopyImagesProps {
  show: boolean;
  onHide: () => void;
  stores: Store[];
  onCopy: (selectedStores: number[], threshold: number, dryRun: boolean) => void;
  loading: boolean;
}

// Styled Components para melhorar a visualização
const StoreCheckboxContainer = styled.div<{ $checked: boolean }>`
  margin-bottom: 0.75rem;
  padding: 1rem;
  border: 2px solid ${props => props.$checked ? '#0d6efd' : '#dee2e6'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.$checked ? '#e7f1ff' : '#fff'};

  &:hover {
    border-color: ${props => props.$checked ? '#0d6efd' : '#adb5bd'};
    background-color: ${props => props.$checked ? '#d4e6ff' : '#f8f9fa'};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .form-check-input {
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.125rem;
    cursor: pointer;
  }

  .form-check-label {
    cursor: pointer;
    width: 100%;
    margin-left: 0.5rem;
  }
`;

const StoreList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 0.5rem;

  /* Estilo da scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const LoadingCard = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  text-align: center;
  min-width: 400px;
  max-width: 500px;
`;

const LoadingIcon = styled.div`
  margin-bottom: 1.5rem;
  
  .spinner-border {
    width: 4rem;
    height: 4rem;
    border-width: 0.4rem;
  }
`;

const LoadingText = styled.div`
  h4 {
    margin-bottom: 1rem;
    color: #0d6efd;
    font-weight: 600;
  }
  
  p {
    color: #6c757d;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

export function ModalCopyImages({ show, onHide, stores, onCopy, loading }: ModalCopyImagesProps) {
  const [selectedStores, setSelectedStores] = useState<number[]>([]);
  const [threshold, setThreshold] = useState(90);

  useEffect(() => {
    console.log('Lojas disponíveis no modal:', stores);
  }, [stores]);

  const handleStoreToggle = (storeId: number) => {
    setSelectedStores(prev => 
      prev.includes(storeId) 
        ? prev.filter(id => id !== storeId)
        : [...prev, storeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStores.length === stores.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(stores.map(s => s.id));
    }
  };

  const handleSubmit = () => {
    if (selectedStores.length === 0) {
      return;
    }
    console.log('Submetendo:', { selectedStores, threshold, dryRun: false });
    onCopy(selectedStores, threshold, false); // Sempre false agora
  };

  const handleClose = () => {
    if (!loading) {
      onHide();
    }
  };

  const getThresholdLabel = () => {
    if (threshold >= 95) return { text: 'Muito Restritivo', color: 'success' };
    if (threshold >= 85) return { text: 'Restritivo', color: 'info' };
    if (threshold >= 75) return { text: 'Moderado', color: 'warning' };
    return { text: 'Permissivo', color: 'danger' };
  };

  const thresholdInfo = getThresholdLabel();

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            <FaStore className="me-2" />
            Copiar Imagens de Produtos
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Aviso importante */}
          <Alert variant="info" className="mb-4">
            <FaCheckCircle className="me-2" />
            <strong>Atenção:</strong> Este processo irá copiar permanentemente as imagens dos produtos 
            selecionados para os produtos da sua loja que ainda não possuem imagem.
          </Alert>

          {/* Threshold */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <FaSliders className="me-2" />
                Similaridade Mínima
              </h6>
              <div className="d-flex align-items-center gap-2">
                <Badge bg={thresholdInfo.color}>{thresholdInfo.text}</Badge>
                <Badge bg="primary">{threshold}%</Badge>
              </div>
            </div>

            <Form.Range
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              min={60}
              max={100}
              step={5}
              disabled={loading}
            />

            <div className="d-flex justify-content-between mt-2">
              <small className="text-muted">60% - Mais permissivo</small>
              <small className="text-muted">100% - Mais restritivo</small>
            </div>

            <Alert variant="info" className="mt-3 mb-0">
              <small>
                <strong>Dica:</strong> Valores mais altos (90-100%) garantem correspondências mais precisas, 
                mas podem encontrar menos produtos. Valores mais baixos (70-85%) encontram mais produtos, 
                mas podem incluir correspondências menos exatas.
              </small>
            </Alert>
          </div>

          <hr />

          {/* Lojas */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <FaStore className="me-2" />
                Selecione as Lojas de Referência
              </h6>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleSelectAll}
                disabled={loading || stores.length === 0}
              >
                {selectedStores.length === stores.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
              </Button>
            </div>

            {stores.length === 0 ? (
              <Alert variant="warning">
                Nenhuma outra loja disponível para seleção.
              </Alert>
            ) : (
              <StoreList>
                {stores.map(store => (
                  <StoreCheckboxContainer 
                    key={store.id}
                    $checked={selectedStores.includes(store.id)}
                    onClick={() => !loading && handleStoreToggle(store.id)}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`store-${store.id}`}
                      checked={selectedStores.includes(store.id)}
                      onChange={() => {}} // Vazio porque o onClick do container já gerencia
                      disabled={loading}
                      label={
                        <div>
                          <strong style={{ fontSize: '1rem' }}>{store.trading_name}</strong>
                          {store.company_name && store.company_name !== store.trading_name && (
                            <>
                              <br />
                              <small className="text-muted">{store.company_name}</small>
                            </>
                          )}
                          <br />
                          <small className="text-muted">ID: {store.id}</small>
                        </div>
                      }
                    />
                  </StoreCheckboxContainer>
                ))}
              </StoreList>
            )}

            {selectedStores.length > 0 && (
              <Alert variant="success" className="mt-3 mb-0">
                <small>
                  <strong>{selectedStores.length}</strong> loja(s) selecionada(s): {' '}
                  {stores
                    .filter(s => selectedStores.includes(s.id))
                    .map(s => s.trading_name)
                    .join(', ')}
                </small>
              </Alert>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || selectedStores.length === 0}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processando...
              </>
            ) : (
              <>
                <FaPlay className="me-2" />
                Copiar Imagens
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Loading Overlay com informações detalhadas */}
      {loading && (
        <LoadingOverlay>
          <LoadingCard>
            <LoadingIcon>
              <Spinner animation="border" variant="primary" />
            </LoadingIcon>
            
            <LoadingText>
              <h4>Copiando Imagens...</h4>
              <p>
                Estamos processando sua solicitação. Este processo pode levar alguns minutos 
                dependendo da quantidade de produtos.
              </p>
              <p className="mb-0">
                <strong>Por favor, aguarde e não feche esta janela.</strong>
              </p>
            </LoadingText>

            <ProgressBar 
              animated 
              now={100} 
              variant="primary" 
              style={{ height: '8px', borderRadius: '4px' }}
            />

            <div className="mt-3">
              <small className="text-muted">
                Analisando produtos e copiando imagens correspondentes...
              </small>
            </div>
          </LoadingCard>
        </LoadingOverlay>
      )}
    </>
  );
}